/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import React, { useEffect, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import { MultiSelectElement } from "react-hook-form-mui";
import { useFormContext, useWatch } from "react-hook-form";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { fetchCollection, queryKeys } from "../../services/ReactQuery/reactQueryService";
import { getMediaObjectFormattedDescription } from "../../services/mediaObjectService";
import { MediaObject } from "../../types/MediaObject";

type AiDocumentsSelectProps = {
  product: {
    id: number;
    type: string;
  } | null;
};

type PromptForgeFormValues = {
  documents: number[];
};

export function AiDocumentsSelect({ product }: AiDocumentsSelectProps) {
  const { setValue } = useFormContext<PromptForgeFormValues>();

  const selectedDocumentIds = useWatch({
    name: "documents",
    defaultValue: [],
  }) as number[];

  const mediaObjectsFilter = useMemo(
    () => (product ? { product: product.type, productId: product.id } : undefined),
    [product]
  );

  const getBaseMimeType = (mediaObject: MediaObject): string => {
    return mediaObject.mimeType?.split(";")[0].trim().toLowerCase() ?? "";
  };

  const isPdfDocument = (mediaObject: MediaObject): boolean => {
    return getBaseMimeType(mediaObject) === "application/pdf";
  };

  const isImageDocument = (mediaObject: MediaObject): boolean => {
    return getBaseMimeType(mediaObject).startsWith("image/");
  };

  const getDocumentIcon = (mediaObject: MediaObject) => {
    if (isPdfDocument(mediaObject)) {
      return <PictureAsPdfOutlinedIcon fontSize="small" />;
    }

    if (isImageDocument(mediaObject)) {
      return <ImageOutlinedIcon fontSize="small" />;
    }

    return null;
  };

  const getDocumentLabel = (mediaObject: MediaObject) => {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
        {getDocumentIcon(mediaObject)}
        <Typography component="span" variant="body2" noWrap>
          {getMediaObjectFormattedDescription(mediaObject)}
        </Typography>
      </Box>
    );
  };

  const { data } = useQuery<any, Error, { mediaObjects: MediaObject[]; documents: MediaObject[] }>({
    queryKey: queryKeys.collection("media_objects", mediaObjectsFilter),
    queryFn: () => fetchCollection("media_objects", mediaObjectsFilter),
    enabled: !!product,
    placeholderData: keepPreviousData,

    select: (collection: any) => {
      const mediaObjects: MediaObject[] = collection?.["hydra:member"] ?? [];

      const documents = mediaObjects
        .filter((mediaObject) => {
          return isPdfDocument(mediaObject) || isImageDocument(mediaObject);
        })
        .sort((a, b) => {
          const ts = (mediaObject: MediaObject) =>
            mediaObject.created ? new Date(mediaObject.created).getTime() : -Infinity;

          return (
            ts(b) - ts(a) ||
            a.originalName.localeCompare(b.originalName, undefined, {
              sensitivity: "base",
              numeric: true,
            })
          );
        });

      return {
        mediaObjects,
        documents,
      };
    }
  });

  const toDocumentIds = (value: unknown): number[] => {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.filter((item): item is number => typeof item === "number");
  };

  const mediaObjects = data?.mediaObjects ?? [];
  const documents = data?.documents ?? [];

  const renderSelectedDocuments = (value: unknown) => {
    const documentIds = toDocumentIds(value);

    const selectedDocuments = documentIds
      .map((documentId) => mediaObjects.find((mediaObject) => mediaObject.id === documentId))
      .filter((mediaObject): mediaObject is MediaObject => !!mediaObject);

    if (selectedDocuments.length === 0) {
      return "";
    }

    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, alignItems: "center" }}>
        {selectedDocuments.map((mediaObject) => (
          <Box
            key={mediaObject.id}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              maxWidth: "100%",
            }}
          >
            {getDocumentIcon(mediaObject)}
            <Typography component="span" variant="body2" noWrap>
              {getMediaObjectFormattedDescription(mediaObject)}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  useEffect(() => {
    if (!product) {
      setValue("documents", []);
    }
  }, [product, setValue]);

  useEffect(() => {
    const validDocumentIds = new Set(documents.map((document) => document.id));
    const nextSelectedDocumentIds = selectedDocumentIds.filter((id) => validDocumentIds.has(id));

    if (nextSelectedDocumentIds.length !== selectedDocumentIds.length) {
      setValue("documents", nextSelectedDocumentIds);
    }
  }, [documents, selectedDocumentIds, setValue]);

  if (!product || documents.length === 0) {
    return null;
  }

  return (
    <MultiSelectElement
      fullWidth
      label="Dokumente einbeziehen"
      name="documents"
      options={documents.map((mediaObject) => ({
        id: mediaObject.id,
        label: getDocumentLabel(mediaObject),
      }))}
      showCheckbox={true}
      renderValue={renderSelectedDocuments}
    />
  );
}
